import React from 'react';

// 8 connector positions around a circle (N, NE, E, SE, S, SW, W, NW)
const getConnectorXY = (angle, radius, cx, cy) => {
  const rad = (angle - 90) * (Math.PI / 180); // -90 so 0° = top (North)
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
};

const CONNECTOR_R = 5;

const CircleNodePreview = ({
  diameter = 100,
  fillColor = '#F5C542',
  fillColorHigh,
  strokeColor = '#D4A843',
  connectors = [],
  splitView = false,
  vLow,
  vHigh,
  icon = '',
  name = '',
  onConnectorClick,
  interactive = false,
  scale = 1,
}) => {
  const pad = 24;
  const r = diameter / 2;
  const cx = pad + r;
  const cy = pad + r;
  const svgSize = (diameter + pad * 2) * scale;
  const safeId = (name || 'node').replace(/[^a-zA-Z0-9]/g, '_');

  return (
    <svg
      width={svgSize}
      height={svgSize}
      viewBox={`0 0 ${diameter + pad * 2} ${diameter + pad * 2}`}
      className="shape-preview-svg"
    >
      <defs>
        {/* Split gradient for transformer nodes */}
        {splitView && fillColorHigh && (
          <linearGradient id={`split-${safeId}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="50%" stopColor={fillColor} />
            <stop offset="50%" stopColor={fillColorHigh} />
          </linearGradient>
        )}
      </defs>

      {/* Main circle */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={splitView && fillColorHigh ? `url(#split-${safeId})` : fillColor}
        stroke={strokeColor}
        strokeWidth={2.5}
      />

      {/* Split divider line for transformer nodes */}
      {splitView && (
        <line
          x1={cx}
          y1={cy - r}
          x2={cx}
          y2={cy + r}
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
      )}

      {/* Voltage labels for split view */}
      {splitView && (
        <>
          <text
            x={cx - r / 2}
            y={cy - 6}
            textAnchor="middle"
            fontSize="10"
            fill="#1a1a2e"
            fontFamily="JetBrains Mono, monospace"
            fontWeight="600"
          >
            {vLow}kV
          </text>
          <text
            x={cx + r / 2}
            y={cy - 6}
            textAnchor="middle"
            fontSize="10"
            fill="#1a1a2e"
            fontFamily="JetBrains Mono, monospace"
            fontWeight="600"
          >
            {vHigh}kV
          </text>
        </>
      )}

      {/* Single voltage label */}
      {!splitView && (
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontSize="11"
          fill="#1a1a2e"
          fontFamily="JetBrains Mono, monospace"
          fontWeight="600"
        >
          {vLow}kV
        </text>
      )}

      {/* Node name */}
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        fontSize="9"
        fill="#1a1a2e"
        fontFamily="Space Grotesk, sans-serif"
        fontWeight="500"
        opacity="0.7"
      >
        {name}
      </text>

      {/* Transformer symbol for split nodes */}
      {splitView && (
        <text
          x={cx}
          y={cy + 26}
          textAnchor="middle"
          fontSize="8"
          fill="#1a1a2e"
          fontFamily="Work Sans, sans-serif"
          opacity="0.5"
        >
          ⏚ XFMR
        </text>
      )}

      {/* Diameter label */}
      <text
        x={cx}
        y={cy + r + 18}
        textAnchor="middle"
        fontSize="9"
        fill="#4a5568"
        fontFamily="JetBrains Mono, monospace"
      >
        ⌀{diameter}px
      </text>

      {/* Connector points */}
      {connectors.map((c) => {
        const pos = getConnectorXY(c.angle, r, cx, cy);
        const isFav = c.favorite;
        const isEnabled = c.enabled;

        return (
          <g
            key={c.id}
            className={interactive ? 'connector-interactive' : ''}
            onClick={() => interactive && onConnectorClick && onConnectorClick(c.id)}
            style={{ cursor: interactive ? 'pointer' : 'default' }}
          >
            {/* Favorite ring */}
            {isFav && (
              <circle
                cx={pos.x}
                cy={pos.y}
                r={CONNECTOR_R + 3}
                fill="none"
                stroke={strokeColor}
                strokeWidth={1.5}
                opacity={0.5}
              />
            )}
            {/* Connector dot */}
            <circle
              cx={pos.x}
              cy={pos.y}
              r={CONNECTOR_R}
              fill={isEnabled ? strokeColor : '#2d3436'}
              stroke={isEnabled ? strokeColor : '#4a5568'}
              strokeWidth={isEnabled ? 0 : 1.5}
              opacity={isEnabled ? (isFav ? 1 : 0.5) : 0.2}
            />
            {/* Label */}
            {interactive && (
              <text
                x={pos.x + (c.angle > 90 && c.angle < 270 ? -12 : 12)}
                y={pos.y + 3}
                textAnchor={c.angle > 90 && c.angle < 270 ? 'end' : 'start'}
                fontSize="8"
                fill="#8a9a95"
                fontFamily="JetBrains Mono, monospace"
              >
                {c.label}
              </text>
            )}
            {/* Favorite star */}
            {isFav && (
              <text
                x={pos.x}
                y={pos.y + 3}
                textAnchor="middle"
                fontSize="7"
                fill="#1a1a2e"
                fontWeight="700"
              >
                ★
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default CircleNodePreview;
